import ErrorMessage from 'components/ErrorMessage'
import FormLabel from 'components/FormLabel'
import InputField from 'components/InputField'
import { states } from 'constants/states'
import { InvestingPersonEnum, RetirementAccountEnum } from 'interfaces'
import React, { ChangeEvent } from 'react'
import PrimaryFieldsSection from './PrimaryFieldsSection'

const TrustIra = (props: any) => {
  return (
    <>
      <div className="py-4 text-left text-2xl font-semibold text-gray-900 md:py-12 md:pb-10 md:text-4xl">
        {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
          props.getValues('investingProfile.retirementAccountType') === 'TRUST_ACCOUNT' &&
          'Trust details...'}
        {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
          props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT &&
          'Retirement Account details...'}
      </div>

      <div className="text-base leading-5 text-gray-500">
        {props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
          props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT &&
          'There are several types of individual retirement accounts, and the information required for and by each type varies. Please provide the information below for the IRA program or custodian and the contact person. A representative from Cultivate Capital will reach out to you, and the contact person at the IRA if needed, for additional information before your transaction is finalized.'}
      </div>

      <div className="mb-4 flex text-xl">
        <div
          className={`w-1/2 border-b-2 pb-2 pr-4 pt-4 hover:cursor-pointer lg:w-auto ${
            props.showPrimary ? 'border-primary-600 font-semibold text-primary-600' : 'border-gray-200'
          }`}
          onClick={() => props.setShowPrimary(true)}
        >
          {props.getValues('investingProfile.retirementAccountType') === 'TRUST_ACCOUNT' && 'Authorized Individual'}
          {props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT &&
            'Retirement Account Holder'}
        </div>
        <div
          className={`w-1/2 border-b-2 pb-2 pt-4 hover:cursor-pointer lg:w-auto ${
            !props.showPrimary ? 'border-primary-600 font-semibold text-primary-600' : 'border-gray-200'
          }`}
          onClick={() => props.setShowPrimary(false)}
        >
          {props.getValues('investingProfile.retirementAccountType') === 'TRUST_ACCOUNT' && 'Trust Account'}
          {props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT &&
            'IRA'}
        </div>
      </div>

      <PrimaryFieldsSection {...props} />

      {/* TRUST ACCOUNT */}

      {!props.showPrimary &&
        props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
        props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.TRUST_ACCOUNT && (
          <>
            <div className="my-1 w-full">
              <FormLabel labelName={'Trust Name'} />
              <InputField
                registerField={props.registerField}
                fieldId={'entityDetails.name'}
                required={true}
                validate={{
                  minLength: (value: string) => value.length > 1,
                  validName: (value: string) => /^[A-Za-z-'\s]+$/.test(value),
                }}
                placeholder={'Trust Name'}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  props.clearErrors('entityDetails.name')
                  props.setValue('entityDetails.name', e.target.value)
                }}
              />

              {props.errors.entityDetails?.name?.type === 'minLength' && (
                <ErrorMessage message={'Minimum of two characters.'} />
              )}
              {props.errors.entityDetails?.name?.type === 'validName' && (
                <ErrorMessage message={'Not a valid trust name.'} />
              )}
              {props.errors.entityDetails?.name?.type === 'required' && (
                <ErrorMessage message={'Trust name is required.'} />
              )}
            </div>

            <div className="flex flex-col items-start justify-between gap-0 md:gap-4 lg:flex-row">
              <div className="my-1 w-full">
                <FormLabel labelName={'Trust Tax ID Number'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.ein'}
                  validate={{
                    minLength: (value: string) => value.length === 9,
                    validateInput: (value: string) => {
                      return !value.includes('#')
                    },
                  }}
                  control={props.control}
                  maskedInput={'pattern'}
                  format={'#########'}
                  value={props.getValues('entityDetails.ein')}
                  mask="#"
                  maskType={'pattern'}
                  allowEmptyFormatting={true}
                  required={true}
                  onChange={(e: any) => {
                    props.clearErrors('entityDetails.ein')
                    props.setValue('entityDetails.ein', e.floatValue)
                  }}
                />
              </div>
            </div>

            {props.errors.entityDetails?.ein?.type === 'minLength' && (
              <ErrorMessage message={'Trust tax ID should have 9 numbers.'} />
            )}
            {props.errors.entityDetails?.ein?.type === 'validateInput' && (
              <ErrorMessage message={'Not a valid trust tax ID.'} />
            )}
            {props.errors.entityDetails?.ein?.type === 'required' && (
              <ErrorMessage message={'Trust name is required.'} />
            )}

            <div className="my-1 w-full">
              <FormLabel labelName={'Street Address 1'} />
              <InputField
                registerField={props.registerField}
                fieldId={'entityDetails.addressInfo.Street1'}
                placeholder={'123 Maple St.'}
                required={true}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  props.clearErrors('entityDetails.addressInfo.Street1')
                  props.setValue('entityDetails.addressInfo.Street1', e.target.value)
                }}
              />
              {props.errors.entityDetails?.addressInfo?.Street1?.type === 'required' && (
                <ErrorMessage message={'Street Address 1 is required'} />
              )}
            </div>

            <div className="my-1 w-full">
              <FormLabel labelName={'Street Address 2'} />
              <InputField
                registerField={props.registerField}
                required={false}
                fieldId={'entityDetails.addressInfo.Street2'}
                placeholder={'Optional'}
              />
            </div>

            <div className="flex flex-col items-start justify-between gap-0 md:gap-4 lg:flex-row">
              <div className="my-1 w-full">
                <FormLabel labelName={'Country'} />
                <select
                  name="country"
                  {...props.registerField('entityDetails.addressInfo.country', { required: true })}
                  className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                  onChange={(e) => {
                    props.setValue('entityDetails.addressInfo.country', e.target.value)
                  }}
                  defaultValue={'USA'}
                >
                  <option selected value={'USA'}>
                    United States
                  </option>
                </select>
              </div>
              <div className="my-1 w-full">
                <FormLabel labelName={'City'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.addressInfo.city'}
                  placeholder={'City'}
                  required={true}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    props.clearErrors('entityDetails.addressInfo.city')
                    props.setValue('entityDetails.addressInfo.city', e.target.value.replace(/[^A-Za-z +]/gi, ''))
                  }}
                />
                {props.errors.entityDetails?.addressInfo?.city?.type === 'required' && (
                  <ErrorMessage message={'City is required'} />
                )}
              </div>

              <div className="my-1 w-full">
                <FormLabel labelName={'State / Province'} />
                {props.getValues('entityDetails.addressInfo.country') === 'USA' ? (
                  <select
                    name="state"
                    {...props.registerField('entityDetails.addressInfo.state', { required: true })}
                    className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                    onChange={(event) => {
                      props.setValue('entityDetails.addressInfo.state', event.target.value)
                      props.clearErrors('entityDetails.addressInfo.state')
                    }}
                  >
                    <option value="" disabled hidden>
                      Please select State
                    </option>
                    {states.map((state, index) => {
                      return (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      )
                    })}
                  </select>
                ) : (
                  <InputField
                    registerField={props.registerField}
                    fieldId={'entityDetails.addressInfo.state'}
                    placeholder={'Province'}
                    required={true}
                  />
                )}
              </div>

              <div className="my-2 w-full md:my-1">
                <FormLabel labelName={'ZIP / Postal'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.addressInfo.postalCode'}
                  placeholder={'12345'}
                  required={true}
                  control={props.control}
                  format=""
                  mask="_"
                  maskedInput={'numeric'}
                  value={props.getValues('entityDetails.addressInfo.postalCode')}
                  validate={{
                    maxLength: (value: string) => value.length === 5 || value.length === 9,
                  }}
                  onChange={(e: any) => {
                    props.clearErrors('entityDetails.addressInfo.postalCode')
                    props.setValue('entityDetails.addressInfo.postalCode', e.value)
                  }}
                />
                {props.errors.entityDetails?.addressInfo?.postalCode?.type === 'maxLength' && (
                  <ErrorMessage message={'Must input a valid ZIP/Postal Code.'} />
                )}
                {props.errors.entityDetails?.addressInfo?.postalCode?.type === 'required' && (
                  <ErrorMessage message={'ZIP code is required'} />
                )}
              </div>
            </div>
          </>
        )}

      {/* RETIREMENT ACCOUNT */}

      {!props.showPrimary &&
        props.getValues('investingProfile.option') === InvestingPersonEnum.RETIREMENT &&
        props.getValues('investingProfile.retirementAccountType') === RetirementAccountEnum.RETIREMENT_ACCOUNT && (
          <>
            <div className="flex flex-col items-start justify-between gap-0 md:gap-4 lg:flex-row">
              <div className="my-1 w-full">
                <FormLabel labelName={'Name of Custodian'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.nameOfCustodian'}
                  required={true}
                  validate={{
                    minLength: (value: string) => value.length > 2,
                    validName: (value: string) => /^[A-Za-z-'\s]+$/.test(value),
                  }}
                  placeholder={'Retirement account custodian'}
                  value={props.watch('entityDetails.nameOfCustodian')}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    props.clearErrors('entityDetails.nameOfCustodian')
                    props.setValue('entityDetails.nameOfCustodian', e.target.value)
                  }}
                />
                {props.errors.entityDetails?.nameOfCustodian?.type === 'minLength' && (
                  <ErrorMessage message={'Minimum of two characters.'} />
                )}
                {props.errors.entityDetails?.nameOfCustodian?.type === 'validName' && (
                  <ErrorMessage message={'Not a valid name.'} />
                )}
                {props.errors.entityDetails?.nameOfCustodian?.type === 'required' && (
                  <ErrorMessage message={'Name is required'} />
                )}
              </div>

              <div className="my-1 w-full">
                <FormLabel labelName={'Email Address of IRA Contact Person'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.emailOfIraContact'}
                  type={'email'}
                  validate={{
                    validEmail: (value: string) => /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/.test(value),
                  }}
                  placeholder={'sample@email.com'}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    props.clearErrors('entityDetails.emailOfIraContact')
                    props.setValue('entityDetails.emailOfIraContact', e.target.value)
                  }}
                />
                {props.errors.entityDetails?.emailOfIraContact?.type === 'validEmail' && (
                  <ErrorMessage message={'Not a valid email.'} />
                )}
              </div>
            </div>

            <div className="flex flex-col items-start justify-between gap-0 md:gap-4 lg:flex-row">
              <div className="my-1 w-full">
                <FormLabel labelName={'Retirement Account Name'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.name'}
                  required={true}
                  validate={{
                    minLength: (value: string) => value.length > 1,
                    validName: (value: string) => /^[A-Za-z-'\s]+$/.test(value),
                  }}
                  placeholder={'Retirement Account Name'}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    props.clearErrors('entityDetails.name')
                    props.setValue('entityDetails.name', e.target.value)
                  }}
                />

                {props.errors.entityDetails?.name?.type === 'minLength' && (
                  <ErrorMessage message={'Minimum of two characters.'} />
                )}
                {props.errors.entityDetails?.name?.type === 'validName' && (
                  <ErrorMessage message={'Not a valid retirement account name.'} />
                )}
                {props.errors.entityDetails?.name?.type === 'required' && (
                  <ErrorMessage message={'Retirement account name is required.'} />
                )}
              </div>
              <div className="my-1 w-full">
                <FormLabel labelName={'Retirement Account Tax ID number'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.ein'}
                  validate={{
                    minLength: (value: string) => value.length === 9,
                    validateInput: (value: string) => {
                      return !value.includes('#')
                    },
                  }}
                  control={props.control}
                  maskedInput={'pattern'}
                  format={'#########'}
                  value={props.getValues('entityDetails.ein')}
                  mask="#"
                  maskType={'pattern'}
                  allowEmptyFormatting={true}
                  required={true}
                  onChange={(e: any) => {
                    props.clearErrors('entityDetails.ein')
                    props.setValue('entityDetails.ein', e.floatValue)
                  }}
                />
                {props.errors.entityDetails?.ein?.type === 'minLength' && (
                  <ErrorMessage message={'Retirement account tax ID should have 9 numbers.'} />
                )}
                {props.errors.entityDetails?.ein?.type === 'validateInput' && (
                  <ErrorMessage message={'Not a valid retirement account tax ID.'} />
                )}
                {props.errors.entityDetails?.ein?.type === 'required' && (
                  <ErrorMessage message={'Retirement account tax ID is required.'} />
                )}
              </div>

              <div className="my-1 w-full">
                <FormLabel labelName={'State of Formation'} />
                {props.getValues('entityDetails.addressInfo.country') === 'USA' ? (
                  <select
                    name="state"
                    {...props.registerField('entityDetails.addressInfo.regionOfFormation', { required: true })}
                    className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                    onChange={(event) => {
                      props.setValue('entityDetails.addressInfo.regionOfFormation', event.target.value)
                      props.clearErrors('entityDetails.addressInfo.regionOfFormation')
                    }}
                  >
                    <option value="" disabled hidden>
                      Please select State of Formation
                    </option>
                    {states.map((state, index) => {
                      return (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      )
                    })}
                  </select>
                ) : (
                  <InputField
                    registerField={props.registerField}
                    fieldId={'entityDetails.addressInfo.regionOfFormation'}
                    placeholder={'Province'}
                    required={true}
                  />
                )}
              </div>
            </div>

            <div className="my-1 w-full">
              <FormLabel labelName={'Street Address 1'} />
              <InputField
                registerField={props.registerField}
                fieldId={'entityDetails.addressInfo.Street1'}
                placeholder={'123 Maple St.'}
                required={true}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  props.clearErrors('entityDetails.addressInfo.Street1')
                  props.setValue('entityDetails.addressInfo.Street1', e.target.value)
                }}
              />
              {props.errors.entityDetails?.addressInfo?.Street1?.type === 'required' && (
                <ErrorMessage message={'Street Address 1 is required'} />
              )}
            </div>

            <div className="my-1 w-full">
              <FormLabel labelName={'Street Address 2'} />
              <InputField
                registerField={props.registerField}
                required={false}
                fieldId={'entityDetails.addressInfo.Street2'}
                placeholder={'Optional'}
              />
            </div>

            <div className="flex flex-col items-start justify-between gap-0 md:gap-4 lg:flex-row">
              <div className="my-1 w-full">
                <FormLabel labelName={'Country'} />
                <select
                  name="country"
                  {...props.registerField('entityDetails.addressInfo.country', { required: true })}
                  className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                  onChange={(e) => {
                    props.setValue('entityDetails.addressInfo.country', e.target.value)
                  }}
                  defaultValue={'USA'}
                >
                  <option selected value={'USA'}>
                    United States
                  </option>
                </select>
              </div>
              <div className="my-1 w-full">
                <FormLabel labelName={'City'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.addressInfo.city'}
                  placeholder={'City'}
                  required={true}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    props.clearErrors('entityDetails.addressInfo.city')
                    props.setValue('entityDetails.addressInfo.city', e.target.value.replace(/[^A-Za-z +]/gi, ''))
                  }}
                />
                {props.errors.entityDetails?.addressInfo?.city?.type === 'required' && (
                  <ErrorMessage message={'City is required'} />
                )}
              </div>

              <div className="my-1 w-full">
                <FormLabel labelName={'State / Province'} />
                {props.getValues('entityDetails.addressInfo.country') === 'USA' ? (
                  <select
                    name="state"
                    {...props.registerField('entityDetails.addressInfo.state', { required: true })}
                    className="mt-1 h-[42px] w-full rounded-md border border-gray-300 bg-gray-50 px-1 py-2 text-base leading-5"
                    onChange={(event) => {
                      props.setValue('entityDetails.addressInfo.state', event.target.value)
                      props.clearErrors('entityDetails.addressInfo.state')
                    }}
                  >
                    <option value="" disabled hidden>
                      Please select State
                    </option>
                    {states.map((state, index) => {
                      return (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      )
                    })}
                  </select>
                ) : (
                  <InputField
                    registerField={props.registerField}
                    fieldId={'entityDetails.addressInfo.state'}
                    placeholder={'Province'}
                    required={true}
                  />
                )}
              </div>

              <div className="my-2 w-full md:my-1">
                <FormLabel labelName={'ZIP / Postal'} />
                <InputField
                  registerField={props.registerField}
                  fieldId={'entityDetails.addressInfo.postalCode'}
                  placeholder={'12345'}
                  required={true}
                  control={props.control}
                  format=""
                  mask="_"
                  maskedInput={'numeric'}
                  value={props.getValues('entityDetails.addressInfo.postalCode')}
                  validate={{
                    maxLength: (value: string) => value.length === 5 || value.length === 9,
                  }}
                  onChange={(e: any) => {
                    props.clearErrors('entityDetails.addressInfo.postalCode')
                    props.setValue('entityDetails.addressInfo.postalCode', e.value)
                  }}
                />
                {props.errors.entityDetails?.addressInfo?.postalCode?.type === 'maxLength' && (
                  <ErrorMessage message={'Must input a valid ZIP/Postal Code.'} />
                )}
                {props.errors.entityDetails?.addressInfo?.postalCode?.type === 'required' && (
                  <ErrorMessage message={'ZIP code is required'} />
                )}
              </div>
            </div>
          </>
        )}
    </>
  )
}

export default TrustIra
